import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArchiveIcon, BellIcon, BellOffIcon, CheckIcon, MailIcon, MessageSquareIcon, ShieldAlertIcon, SmartphoneIcon, UndoIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { useNotifications, isAlwaysSent } from '../../contexts/NotificationContext';
import { DOMAIN_LABEL } from '../../data/notifications';
import type { NotificationChannel, NotificationEvent, NotificationPriority } from '../../types/notifications';
const TABS = ['Everything', 'Needs action', 'Security and money', 'Archive'] as const;
const PRIORITY: Record<NotificationPriority, {
  label: string;
  tone: 'danger' | 'warn' | 'neutral';
}> = {
  critical: {
    label: 'Always sent',
    tone: 'danger'
  },
  high: {
    label: 'Important',
    tone: 'warn'
  },
  normal: {
    label: 'Update',
    tone: 'neutral'
  },
  low: {
    label: 'For information',
    tone: 'neutral'
  }
};
const CHANNEL_ICON: Record<NotificationChannel, React.ReactNode> = {
  inApp: <BellIcon aria-hidden className="h-3.5 w-3.5" data-id="element-11091" data-mp="59vyifico9c9" />,
  email: <MailIcon aria-hidden className="h-3.5 w-3.5" data-id="element-11092" data-mp="579qkerjrrti" />,
  push: <SmartphoneIcon aria-hidden className="h-3.5 w-3.5" data-id="element-11093" data-mp="a98sdk7n2goc" />,
  sms: <MessageSquareIcon aria-hidden className="h-3.5 w-3.5" data-id="element-11094" data-mp="f7k6i74yvdcd" />
};
const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  inApp: 'In Campuzon',
  email: 'Email',
  push: 'Push',
  sms: 'SMS'
};

/**
 * The destination the bell has always implied. Grouped by domain, with security,
 * safety and money held above everything else because those are the ones a
 * person cannot afford to miss.
 */
export function Notifications() {
  const {
    inbox,
    archived,
    unreadCount,
    unreadByDomain,
    markRead,
    markUnread,
    markAllRead,
    archive,
    restore
  } = useNotifications();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Everything');
  const [openId, setOpenId] = useState<string | null>(null);
  const items = useMemo(() => {
    if (tab === 'Archive') return archived;
    if (tab === 'Needs action') return inbox.filter(item => item.to && !item.read);
    if (tab === 'Security and money') return inbox.filter(item => isAlwaysSent(item.domain));
    return inbox;
  }, [tab, inbox, archived]);
  const domains = useMemo(() => {
    const groups = new Map<string, NotificationEvent[]>();
    for (const item of items) {
      const list = groups.get(item.domain) ?? [];
      list.push(item);
      groups.set(item.domain, list);
    }
    // Always-sent domains first, then by how many are unread.
    return [...groups.entries()].sort(([a, aItems], [b, bItems]) => {
      const priority = Number(isAlwaysSent(b as never)) - Number(isAlwaysSent(a as never));
      if (priority !== 0) return priority;
      return bItems.filter(i => !i.read).length - aItems.filter(i => !i.read).length;
    });
  }, [items]);
  return <div className="space-y-6" data-id="element-11095" data-mp="7u1rl4iu4t64">
      <PageHeader eyebrow="Notifications" title={unreadCount > 0 ? `${unreadCount} unread` : 'Nothing unread'} description="Everything Campuzon has told you, grouped by where it came from. Security, safety and money sit at the top because they are the ones you cannot switch off." actions={<div className="flex flex-wrap gap-2" data-id="element-11097" data-mp="3gby6orsdx63i">
            <Button variant="secondary" size="sm" onClick={markAllRead} disabled={unreadCount === 0} data-id="element-11098" data-mp="3l9q1puobt8bx">
              Mark all read
            </Button>
            <Link to="/settings" data-id="element-11099" data-mp="3baim10u4d1ln">
              <Button variant="ghost" size="sm" data-id="element-11100" data-mp="3b9pdpvfii41e">
                Notification settings
              </Button>
            </Link>
          </div>} data-id="element-11096" data-mp="7t0eyvm67yma" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Notification views" data-id="element-11101" data-mp="14i84eepwurvh" />

      {items.length === 0 ? <Panel data-id="element-11102" data-mp="35m0ik3u7v2tx">
          <EmptyState icon={<BellOffIcon className="h-5 w-5" data-id="element-11104" data-mp="35lqpg7dvkfk0" />} title={tab === 'Archive' ? 'Nothing archived' : tab === 'Needs action' ? 'Nothing is waiting on you' : 'Nothing here yet'} description={tab === 'Archive' ? 'Archiving a notification keeps the record and takes it out of your inbox. Nothing is deleted.' : tab === 'Needs action' ? 'Every notification with something to do has been read and acted on.' : 'As you apply for things, join groups, order from the marketplace and set reminders, the record of what happened arrives here.'} data-id="element-11103" data-mp="35mk0vlns3t8h" />
        </Panel> : <div className="space-y-5" data-id="element-11105" data-mp="23vvf8kci21p1">
          {domains.map(([domain, list]) => {
        const unread = list.filter(item => !item.read).length;
        return <Panel key={domain} title={DOMAIN_LABEL[domain as keyof typeof DOMAIN_LABEL] ?? domain} description={isAlwaysSent(domain as never) ? 'Always sent. Your preferences cannot switch this off.' : `${list.length} notification${list.length === 1 ? '' : 's'}${unreadByDomain[domain] ? ` · ${unreadByDomain[domain]} unread` : ''}`} bodyClassName="p-0" data-id="element-11106" data-mp="1ouesjducjdt2">
                <ul className="divide-y divide-line" data-id="element-11107" data-mp="1b0e2r9ldh14u">
                  {list.map(item => <li key={item.id} className={item.read ? 'px-5 py-4 sm:px-6' : 'bg-brand-50/40 px-5 py-4 sm:px-6'} data-id="element-11108" data-mp="1fziarwwk8stl">
                      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3" data-id="element-11109" data-mp="ncea244i30nm">
                        <div className="min-w-0" data-id="element-11110" data-mp="safrylq6wtxi">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-id="element-11111" data-mp="kat26u2ekmma">
                            {!item.read && <span aria-label="Unread" className="h-2 w-2 shrink-0 rounded-full bg-brand-700" data-id="element-11112" data-mp="fbouk7v4mwy2" />}
                            <p className="text-body-lg font-semibold text-ink" data-id="element-11113" data-mp="p4dtk39hqici">{item.title}</p>
                            <Badge tone={PRIORITY[item.priority].tone} data-id="element-11114" data-mp="1ea0gptxi0zr">
                              {PRIORITY[item.priority].label}
                            </Badge>
                          </div>
                          <p className="mt-1 max-w-[76ch] text-body-sm text-ink" data-id="element-11115" data-mp="b8a38l1qmu5b">{item.detail}</p>
                          <p className="cz-num mt-1.5 text-body-sm text-ink-muted" data-id="element-11116" data-mp="6dwcivy0tb6z">
                            {item.time}
                            {item.reference ? ` · ${item.reference}` : ''}
                          </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-2" data-id="element-11117" data-mp="1p9g98g2i4dt3">
                          {item.to && item.actionLabel && <Link to={item.to} onClick={() => markRead(item.id)} data-id="element-11118" data-mp="1u6y8feqpvenw">
                              <Button size="sm" variant="secondary" data-id="element-11119" data-mp="1gbcovn395n2v">
                                {item.actionLabel}
                              </Button>
                            </Link>}
                          <Button size="sm" variant="ghost" onClick={() => item.read ? markUnread(item.id) : markRead(item.id)} data-id="element-11120" data-mp="2831zx0a7h8wo">
                            {item.read ? 'Mark unread' : 'Mark read'}
                          </Button>
                          {item.archived ? <Button size="sm" variant="ghost" onClick={() => restore(item.id)} data-id="element-11121" data-mp="2qxqnc91wz0o3">
                              <UndoIcon aria-hidden className="h-4 w-4" data-id="element-11122" data-mp="2vs4pcub3061q" />
                              Restore
                            </Button> : <Button size="sm" variant="ghost" onClick={() => archive(item.id)} data-id="element-11123" data-mp="227md8e1xfcfs">
                              <ArchiveIcon aria-hidden className="h-4 w-4" data-id="element-11124" data-mp="1xa434kc6ar67" />
                              Archive
                            </Button>}
                        </div>
                      </div>

                      <button type="button" onClick={() => setOpenId(openId === item.id ? null : item.id)} aria-expanded={openId === item.id} className="mt-2 text-body-sm font-medium text-brand-700 underline decoration-brand-700/40 underline-offset-2 transition-colors duration-150 ease-out hover:text-brand-800" data-id="element-11125" data-mp="2kzf5u7d32vmz">
                        {openId === item.id ? 'Hide delivery' : 'How this reached you'}
                      </button>

                      {openId === item.id && <ul className="mt-2.5 space-y-2 rounded-md border border-line bg-canvas p-3" data-id="element-11126" data-mp="3g891otgyc9q2">
                          {item.deliveries.map(delivery => <li key={delivery.channel} className="flex items-start gap-2.5" data-id="element-11127" data-mp="3l6agh27chmvq">
                              <span className="mt-0.5 text-ink-subtle" data-id="element-11128" data-mp="3pzmwwp1vende">
                                {CHANNEL_ICON[delivery.channel]}
                              </span>
                              <span className="min-w-0" data-id="element-11129" data-mp="2skqdzzq5oio">
                                <span className="text-body-sm text-ink" data-id="element-11130" data-mp="7sp97wo3uvc2">
                                  {CHANNEL_LABEL[delivery.channel]} —{' '}
                                  <span className={delivery.state === 'delivered' ? 'font-medium text-ok-700' : delivery.state === 'failed' ? 'font-medium text-danger-700' : 'font-medium text-ink-muted'} data-id="element-11131" data-mp="cqgzynd0kbeo">
                                    {delivery.state === 'not_configured' ? 'not available' : delivery.state}
                                  </span>
                                </span>
                                {delivery.note && <span className="mt-0.5 block text-caption text-ink-subtle" data-id="element-11132" data-mp="1r6zrtpqcmyz7">
                                    {delivery.note}
                                  </span>}
                              </span>
                            </li>)}
                        </ul>}
                    </li>)}
                </ul>
              </Panel>;
      })}
        </div>}

      <Panel title="What is always sent" data-id="element-11133" data-mp="3ufi9to8igtba">
        <ul className="space-y-2" data-id="element-11134" data-mp="3piicq2tn30wb">
          {['A sign-in from a new device, or a change to your password, email or Notes PIN.', 'Safety alerts affecting you on or around your campus.', 'Anything that moves value in your ledger, including reversals and disputes.'].map(line => <li key={line} className="flex items-start gap-2 text-body-sm text-ink" data-id="element-11135" data-mp="h26sy7e8aaxu">
              <ShieldAlertIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-11136" data-mp="d2o799el1vty" />
              {line}
            </li>)}
        </ul>
        <p className="mt-3 flex items-start gap-2 border-t border-line pt-3 text-caption text-ink-subtle" data-id="element-11137" data-mp="3btj1zhs0m5v">
          <CheckIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-11138" data-mp="10rh9fnlsszx0" />
          Switching a topic off stops the email, push and SMS. The record still arrives here, so
          muting something never means losing it.
        </p>
      </Panel>
    </div>;
}